import { Eye, ExternalLink, Heart, ImageOff, Play } from 'lucide-react';
import { PLATFORM_LABEL } from '../../../lib/reporting/socialExplorer';
import type { SocialBestPost } from '../../../types/socialReporting';

// Organic Social Reporting — the "best post" block under a platform mini-card.
// The channel's top post by engagement in the period: thumbnail, caption snippet,
// the winning engagement figure, and a permalink. FB + IG only (the caller never
// renders this for GBP — no post analytics, no ranking to make).
export function BestPostCard({ post }: { post: SocialBestPost }) {
  const caption = snippet(post.caption);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
        Best post
      </p>
      <div className="flex gap-3">
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md bg-slate-100">
          {post.thumbnailUrl ? (
            // eslint-disable-next-line jsx-a11y/img-redundant-alt
            <img
              src={post.thumbnailUrl}
              alt="Post thumbnail"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-slate-300">
              <ImageOff className="h-5 w-5" aria-hidden />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm text-slate-700">
            {caption || <span className="italic text-slate-400">No caption</span>}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span className="inline-flex items-center gap-1 font-medium text-slate-700">
              <Heart className="h-3.5 w-3.5 text-rose-500" aria-hidden />
              {post.engagement.toLocaleString()} engagements
            </span>
            {post.views !== null && (
              <span className="inline-flex items-center gap-1 text-slate-500">
                <Eye className="h-3.5 w-3.5" aria-hidden />
                {post.views.toLocaleString()} views
              </span>
            )}
            {post.igReelsAvgWatchTime !== null && post.igReelsAvgWatchTime > 0 && (
              <span className="inline-flex items-center gap-1 text-slate-500" title="Reel average watch time">
                <Play className="h-3 w-3" aria-hidden />
                {formatSeconds(post.igReelsAvgWatchTime)} avg watch
              </span>
            )}
            {post.permalink && (
              <a
                href={post.permalink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-kyck-700 hover:underline"
              >
                View on {PLATFORM_LABEL[post.platform]}
                <ExternalLink className="h-3 w-3" aria-hidden />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function snippet(caption: string, max = 100): string {
  const c = caption.trim().replace(/\s+/g, ' ');
  return c.length <= max ? c : `${c.slice(0, max - 1).trimEnd()}…`;
}

// Watch times arrive in seconds (observed). Sub-minute → "12s", else "1m 05s".
function formatSeconds(sec: number): string {
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}
